import { CLOSED_STAGES, STAGES } from '../../../shared/enums';
import type { Stage } from '../../../shared/enums';

/** Current-state populations, never historical milestones or touch cohorts. */
export { STAGES, CLOSED_STAGES };
export const OPEN_STAGES = STAGES.filter((stage) => !CLOSED_STAGES.includes(stage));
export const NON_LOST_STAGES = STAGES.filter((stage) => stage !== 'closed_lost');
type Staged = { stage: Stage };

export const leadsAtStage = <T extends Staged>(leads: readonly T[], stage: Stage): T[] =>
  leads.filter((lead) => lead.stage === stage);

export const stageCounts = (leads: readonly Staged[]): { stage: Stage; count: number }[] =>
  STAGES.map((stage) => ({ stage, count: leadsAtStage(leads, stage).length }));

export const summarizeLeadStages = (leads: readonly Staged[]) => {
  const current = Object.fromEntries(stageCounts(leads).map(({ stage, count }) => [stage, count])) as Record<Stage, number>;
  return {
    all: leads.length,
    open: OPEN_STAGES.reduce((sum, stage) => sum + current[stage], 0),
    nonLost: NON_LOST_STAGES.reduce((sum, stage) => sum + current[stage], 0),
    current,
  };
};

export interface FunnelRung {
  stage: Stage;
  /** Current membership at or past this stage, excluding Lost. No history is inferred. */
  reached: number;
  /** Ratio to the previous rung; the first rung is the baseline (1). */
  conversion: number;
}

export const buildFunnel = (leads: readonly Staged[]): FunnelRung[] => {
  const { current } = summarizeLeadStages(leads);
  const reached = NON_LOST_STAGES.map((_, i) =>
    NON_LOST_STAGES.slice(i).reduce((sum, stage) => sum + current[stage], 0));
  return NON_LOST_STAGES.map((stage, i) => ({
    stage,
    reached: reached[i] ?? 0,
    conversion: i === 0 ? 1 : reached[i - 1] ? (reached[i] ?? 0) / reached[i - 1]! : 0,
  }));
};
